import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { Observable } from 'rxjs';
import { buildQueryString } from 'src/lib/utilities/api/http';
import {
	DefaultErrorMessage,
	mapResponse,
} from 'src/lib/utilities/api/map-response';
import { PagingArgument } from 'src/lib/utilities/api/patterns/pagination/paging.argument';
import { CurriculumManagerHistoryResponseModel } from '../curriculum-manager-history.model';
import { UpsertGradPlanArgument } from './grad-plan-create.argument';
import {
	GradPlanDetailModel,
	GradPlanRequirementModel,
} from './grad-plan-detail.model';
import {
	GradPlansModel,
	SearchGradPlansArgument,
} from './grad-plan-overview.model';
import { GradPlanRequirementArgument } from './grad-plan-requirement.argument';

@Injectable({
	providedIn: 'root',
})
export class GradPlanService {
	constructor(private httpClient: HttpClient) {}

	public viewGradPlan = (id: number): Observable<GradPlanDetailModel> => {
		return this.httpClient
			.get<{
				graduation_plan: unknown;
			}>(`/api/v1/curriculum/graduation_plan/${id}`)
			.pipe(
				mapResponse(
					(r) => plainToInstance(GradPlanDetailModel, r.graduation_plan),
					{
						errorCode: '71B1C818',
					},
				),
			);
	};

	public searchGradPlans = (
		args: PagingArgument<SearchGradPlansArgument>,
	): Observable<GradPlansModel> => {
		return this.httpClient
			.post<unknown>(`/api/v1/curriculum/graduation_plan/search`, args)
			.pipe(
				mapResponse((r) => plainToInstance(GradPlansModel, r), {
					errorCode: '37F08116',
				}),
			);
	};

	public createGradPlan = (
		args: UpsertGradPlanArgument,
	): Observable<GradPlanDetailModel> => {
		return this.httpClient
			.post<{
				graduation_plan: unknown;
			}>(`/api/v1/curriculum/graduation_plan/create`, args)
			.pipe(
				mapResponse(
					(r) => plainToInstance(GradPlanDetailModel, r.graduation_plan),
					{
						errorCode: '752DA7D9',
						errorMessage: DefaultErrorMessage.Saving,
					},
				),
			);
	};

	public editGradPlan = (
		id: number,
		args: Partial<UpsertGradPlanArgument>,
	): Observable<GradPlanDetailModel> => {
		return this.httpClient
			.post<{
				graduation_plan: unknown;
			}>(`/api/v1/curriculum/graduation_plan/${id}`, args)
			.pipe(
				mapResponse(
					(r) => plainToInstance(GradPlanDetailModel, r.graduation_plan),
					{
						errorCode: '49059449',
						errorMessage: DefaultErrorMessage.Saving,
					},
				),
			);
	};

	// Edit Graduation Plan in postman
	public reactivateGradPlan = (id: number): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/curriculum/graduation_plan/${id}?reactivate=TRUE`,
				{},
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '3D8A1360',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	// Delete Graduation Plan in postman
	public deactivateGradPlan = (id: number): Observable<unknown> => {
		return this.httpClient
			.delete<unknown>(`/api/v1/curriculum/graduation_plan/${id}?delete=FALSE`)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '2616C246',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public deleteGradPlan = (id: number): Observable<unknown> => {
		return this.httpClient
			.delete<unknown>(`/api/v1/curriculum/graduation_plan/${id}`)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '2BCDFBA4',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public viewHistory = (
		id: number,
		start_date: string,
		end_date: string,
		skip: string = '0',
		take: string = '1000',
	): Observable<CurriculumManagerHistoryResponseModel> => {
		let qs = buildQueryString({ skip, take });
		if (start_date && end_date) {
			qs = buildQueryString({ start_date, end_date, skip, take });
		}
		return this.httpClient
			.get<unknown>(`/api/v1/curriculum/graduation_plan/${id}/history${qs}`)
			.pipe(
				mapResponse(
					(r) => plainToInstance(CurriculumManagerHistoryResponseModel, r),
					{
						errorCode: '350BAED5',
					},
				),
			);
	};

	/****************/
	/* Requirements */
	/****************/
	public createRequirement = (
		gradPlanId: number,
		args: GradPlanRequirementArgument,
	): Observable<GradPlanRequirementModel> => {
		return this.httpClient
			.post<{
				requirement: unknown;
			}>(
				`/api/v1/curriculum/graduation_plan/${gradPlanId}/requirement/create`,
				args,
			)
			.pipe(
				mapResponse(
					(r) => plainToInstance(GradPlanRequirementModel, r.requirement),
					{
						errorCode: 'F597A33D',
						errorMessage: DefaultErrorMessage.Saving,
					},
				),
			);
	};

	public editRequirement = (
		gradPlanId: number,
		requirementId: number,
		credits: number,
	): Observable<GradPlanRequirementModel> => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/curriculum/graduation_plan/${gradPlanId}/requirement/${requirementId}`,
				{ credits },
			)
			.pipe(
				mapResponse((r) => plainToInstance(GradPlanRequirementModel, r), {
					errorCode: 'D1D3B974',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public deleteRequirement = (
		gradPlanId: number,
		requirementId: number,
	): Observable<unknown> => {
		return this.httpClient
			.delete<unknown>(
				`/api/v1/curriculum/graduation_plan/${gradPlanId}/requirement/${requirementId}`,
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '43BB7030',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};
}
